import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  HostListener,
  NgZone,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServiceLocatorService } from '@ncs/ncs-lib';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { Subject, BehaviorSubject, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  map,
} from 'rxjs/operators';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────
interface ChatUser {
  userId: string;
  displayName: string;
  email: string;
  online?: boolean;
  unreadCount?: number;
  lastMessage?: string;
  lastTime?: number;
}

interface ChatRoom {
  roomId: string;
  roomName: string;
  createdBy?: string;
  memberUserIds?: string[];
  unreadCount?: number;
  lastMessage?: string;
  lastTime?: number;
  memberDetails?: MemberDetail[];
}
// ── NEW: thin member record returned by /room/members/details ──────────────
interface MemberDetail {
  userId: string;
  displayName: string;
}
interface RoomMemberUpdateEvent {
  type: 'ROOM_MEMBER_UPDATE';
  roomId: string;
  memberDetails: MemberDetail[];
  timestamp: number;
}

interface ReplyContext {
  id?: string;
  fromEmailId: string;
  fromName?: string;
  content: string;
  fileOriginalName?: string;
  fileType?: string;
}

interface ChatMessage {
  _id?: string;
  id?: string | number;
  fromEmailId: string;
  fromName?: string;
  toEmailId?: string;
  roomId?: string;
  content: string;
  type: string;
  timestamp: number;
  status?: string; // SENT | DELIVERED | SEEN
  imageId?: number | string; // skey from doc service
  fileOriginalName?: string;
  fileSize?: number;
  fileType?: string;
  isImages?: boolean;
  fileUrl?: string;
  fileName?: string;
  replyTo?: ReplyContext;
  isSystemMessage?: boolean; // true for SYSTEM type messages (member added/left)
}

interface StatusUpdateEvent {
  type: 'STATUS_UPDATE';
  subType: 'DELIVERED' | 'SEEN';
  fromEmailId: string;
  toEmailId: string;
  timestamp: number;
}
// ── NEW: Group search suggestion interface ────────────────────────────────
interface GroupSuggestion {
  roomId: string;
  roomName: string;
  memberCount: number;
  memberDetails?: MemberDetail[];
  lastMessage?: string;
  lastTime?: number;
  isJoined: boolean;
  createdBy?: string;
  matchScore?: number; // relevance ranking
}
interface AddMemberCandidate {
  userId: string; // email / loginId
  displayName: string;
  email: string;
}

type Tab = 'private' | 'group';
interface PersistedFileDraft {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  lastModified: number;
  savedAt: number;
  data: ArrayBuffer;
}

class FileDraftDbService {
  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = this.openDb();
  }

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('chat_file_drafts', 1);
      req.onupgradeneeded = (ev) => {
        const db = (ev.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('drafts')) {
          db.createObjectStore('drafts', { keyPath: 'id' });
        }
      };
      req.onsuccess = (ev) => resolve((ev.target as IDBOpenDBRequest).result);
      req.onerror = (ev) => reject((ev.target as IDBOpenDBRequest).error);
    });
  }

  private async tx(
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest | void,
  ): Promise<any> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('drafts', mode);
      const store = tx.objectStore('drafts');
      const req = fn(store);
      if (req) {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } else {
        tx.oncomplete = () => resolve(undefined);
        tx.onerror = () => reject(tx.error);
      }
    });
  }

  async save(id: string, file: File): Promise<void> {
    const data = await file.arrayBuffer();
    await this.tx('readwrite', (s) =>
      s.put({
        id,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        lastModified: file.lastModified,
        savedAt: Date.now(),
        data,
      } as PersistedFileDraft),
    );
  }

  async load(id: string): Promise<File | null> {
    const d: PersistedFileDraft | undefined = await this.tx('readonly', (s) =>
      s.get(id),
    );
    if (!d) return null;
    return new File([d.data], d.name, {
      type: d.mimeType,
      lastModified: d.lastModified,
    });
  }

  async delete(id: string): Promise<void> {
    await this.tx('readwrite', (s) => s.delete(id));
  }

  async loadAllForUser(userEmail: string): Promise<Map<string, File>> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const result = new Map<string, File>();
      const tx = db.transaction('drafts', 'readonly');
      const req = tx.objectStore('drafts').openCursor();
      req.onsuccess = (ev) => {
        const cursor = (ev.target as IDBRequest<IDBCursorWithValue>).result;
        if (!cursor) {
          resolve(result);
          return;
        }
        const d: PersistedFileDraft = cursor.value;
        const prefix = userEmail + ':';
        if (d.id.startsWith(prefix)) {
          result.set(
            d.id.slice(prefix.length),
            new File([d.data], d.name, {
              type: d.mimeType,
              lastModified: d.lastModified,
            }),
          );
        }
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }
  /** Delete drafts older than 24 hours to prevent storage bloat. */
  async cleanupStaleDrafts(maxAgeMs = 30 * 60 * 1000): Promise<void> {
    // 30 minutes
    const db = await this.dbPromise;
    const now = Date.now();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('drafts', 'readwrite');
      const req = tx.objectStore('drafts').openCursor();
      req.onsuccess = (ev) => {
        const cursor = (ev.target as IDBRequest<IDBCursorWithValue>).result;
        if (!cursor) {
          resolve();
          return;
        }
        const d: PersistedFileDraft = cursor.value;
        if (now - (d.savedAt ?? d.lastModified) > maxAgeMs) {
          cursor.delete();
        }
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }
}
// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css'],
  host: { style: 'display:block; height:calc(100vh - 75px); overflow:hidden;' },
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('msgBox') private msgBox!: ElementRef;
  @ViewChild('groupSearchInput') private groupSearchInputRef!: ElementRef;

  // ── Identity ──────────────────────────────────────────────────────────
  me = '';
  myName = '';
  private myAppId = '';
  private myOrgId = '';
  private keySuffix = '';
  private sessionId = '0';
  isMobile: boolean = false;
  private get activeChatStorageKey(): string {
    return `chat_active_${this.me}`;
  }
  // ── Server URLs ───────────────────────────────────────────────────────
  private QD = '';
  private CHAT = '';
  private DOC = '';

  imageTimestamp = Date.now();
  readonly MEMBER_DISPLAY_LIMIT = 3;

  // ── UI state ──────────────────────────────────────────────────────────
  activeTab: Tab = 'private';
  users: ChatUser[] = [];
  filteredUsers: ChatUser[] = [];
  selectedUser?: ChatUser;
  rooms: ChatRoom[] = [];
  filteredRooms: ChatRoom[] = [];
  selectedRoom?: ChatRoom;
  messages: ChatMessage[] = [];
  // newMessage = '';
  searchQuery = '';
  newRoomId = '';
  newRoomName = '';
  joinRoomId = '';
  joinRoomInput = '';
  joinRoomError = '';
  joiningRoom = false;
  showCreate = false;
  showJoin = false;
  loadingUsers = false;
  loadingRooms = false;
  loadingMsgs = false;
  sending = false;
  previewImageUrl: string | null = null;
  searchActive = false;
  sidebarOpen = this.checkIsMobile();
  showMobileChat = false; // true when a chat is selected on mobile

  private checkIsMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  private onResize = () => {
    const mobile = this.checkIsMobile();
    this.isMobile = mobile;
    // On desktop always show sidebar; on mobile show sidebar only when no active chat
    if (!mobile) {
      this.sidebarOpen = false; // desktop uses static sidebar, not overlay
    } else if (!this.showMobileChat) {
      this.sidebarOpen = true;
    }
  };
  showEmojiPicker = false;
  showMembersPanel = false;
  memberSearchQuery = '';
  hoveredMsgId: string | null = null;
  createRoomError: string = '';
  showInlineAddMember = false;
  showLeaveGroupModal = false; // Bootstrap modal for leave group confirmation
  leavingGroup = false; // spinner during API call
  private _searchMsgQuery = '';
  filteredMessages: ChatMessage[] = [];
  currentMatchIndex = -1;
  matchesList: number[] = [];
  private doScroll = false;
  // pendingFile: File | null = null;
  // pendingFileError: string | null = null;
  private pendingFiles = new Map<string, File>();
  private pendingFileErrors = new Map<string, string>();
  private fileDraftDb = new FileDraftDbService();
  private pendingFilePreviewUrls = new Map<string, string>();
  private replyingToMap = new Map<string, ChatMessage>();
  private draftMap = new Map<string, string>();
  private draftSavedAt = new Map<string, number>();
  private replyDraftMap = new Map<string, ChatMessage | null>();

  // ── WebSocket ─────────────────────────────────────────────────────────
  private stompClient?: Client;
  private wsConnected = false;
  private roomSubs = new Map<string, any>();

  // ── Polling (3s safety net) ───────────────────────────────────────────
  // WS miss ho jaye to bhi message 3s mein aayega — refresh ki zaroorat nahi
  private pollInterval?: any;
  previewFileName: any;
  groupSearchQuery = '';
  groupSuggestions: GroupSuggestion[] = [];
  showGroupSuggestions = false;
  activeSuggestionIndex = -1;
  searchingGroups = false;
  groupSearchEmpty = false;
  hoveredSuggestion: GroupSuggestion | null = null;
  private groupSearch$ = new Subject<string>();

  // ════════════════════════════════════════════════════════════════════════
  // GROUP SEARCH STATE  ★ NEW ★
  // ════════════════════════════════════════════════════════════════════════

  /** Controls visibility of the Add Member modal */
  showAddMemberModal = false;

  /** User types here to search for candidates */
  addMemberSearchQuery = '';

  /** Real-time suggestions returned from the backend */
  addMemberSuggestions: AddMemberCandidate[] = [];

  /** Shows spinner while suggestion API call is in flight */
  loadingAddMemberSuggestions = false;

  /** User already selected to be added (before final confirmation) */
  selectedAddMemberUser: AddMemberCandidate | null = null;

  /** Feedback message shown in the modal after an add attempt */
  addMemberMessage = '';
  addMemberError = false;

  /** Whether the add-member API call is in progress */
  addingMember = false;

  /** Drives the debounced search pipeline inside the modal */
  private addMemberSearch$ = new Subject<string>();

  /** Teardown helper */
  private destroy$ = new Subject<void>();

  get searchMsgQuery(): string {
    return this._searchMsgQuery;
  }
  set searchMsgQuery(val: string) {
    this._searchMsgQuery = val;
    this.filterMessages();
  }

  constructor(
    private http: HttpClient,
    private locator: ServiceLocatorService,
    private ngZone: NgZone,
  ) {}

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────
  async ngOnInit() {
    this.QD = await this.locator.config.getProperty('SERVER_URL');
    this.CHAT = await this.locator.config.getProperty('CHAT_SERVER_URL');
    this.DOC = await this.locator.config.getProperty('DOC_API');
    this.readCtx();
    if (!this.me) return;
    // ⛔ WAIT for config

    this.isMobile = this.checkIsMobile();
    // On mobile with no active chat, show sidebar by default (WhatsApp style)
    if (this.isMobile) {
      this.sidebarOpen = true;
      this.showMobileChat = false;
    }
    window.addEventListener('resize', this.onResize);

    this.loadUsers();
    this.loadRooms();
    this.connectWS();
    this.markAllDeliveredOnLogin();
    this.initGroupSearchPipeline();
    // app open hote hi pending SENT → DELIVERED
    // this.startPolling(); // har 3s history sync
    this.initAddMemberSearchPipeline();
    this.loadDraftsFromStorage();
  }

  ngAfterViewChecked(): void {
    if (this.doScroll) {
      this.scrollBottom();
      this.doScroll = false;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.stompClient?.deactivate();
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
    for (const url of this.pendingFilePreviewUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.pendingFilePreviewUrls.clear();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // User context (localStorage)
  // ─────────────────────────────────────────────────────────────────────────
  private readCtx(): void {
    this.keySuffix = localStorage.getItem('keySuffix') ?? '';
    this.sessionId = localStorage.getItem(this.keySuffix + 'sessionId') ?? '0';
    let uc: any;
    try {
      const r = localStorage.getItem(this.keySuffix + 'userContext');
      uc = r ? JSON.parse(r) : undefined;
    } catch {
      uc = undefined;
    }
    if (!uc && this.locator?.userContext) uc = this.locator.userContext;
    if (!uc) return;
    this.me = uc.loginId ?? String(uc.userId ?? '');
    this.myName = uc.firstName
      ? `${uc.firstName} ${uc.lastName ?? ''}`.trim()
      : (uc.loginId ?? '');
    // this.myAppId = String(uc.appId ?? uc.applicationId ?? '');
    this.myAppId = String(uc.appId ?? uc.applicationId ?? '');
    this.myOrgId = String(uc.orgId ?? uc.organizationId ?? '');
  }

  url(path: string): string {
    const sid = this.sessionId || '0';
    const qi = path.indexOf('?');
    return qi !== -1
      ? `${this.QD}${path.slice(0, qi)};jsessionid=${sid}${path.slice(qi)}`
      : `${this.QD}${path};jsessionid=${sid}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Polling — 3s safety net
  // ─────────────────────────────────────────────────────────────────────────
  // private startPolling(): void {
  //   this.ngZone.runOutsideAngular(() => {
  //     this.pollInterval = setInterval(() => {
  //       this.ngZone.run(() => this.doPoll());
  //     }, 3000);
  //   });
  // }
  getDocUrl(imageId: number | string | undefined): string {
    if (!imageId) return '';
    // Use 'doc' instead of 'th'
    return `${this.DOC}/${imageId}?t=${this.imageTimestamp}`;
  }
  hasFile(m: ChatMessage): boolean {
    return !!(m.imageId || m.fileUrl);
  }
  isImageFile(m: ChatMessage): boolean {
    return m.fileType === 'IMAGE';
  }

  resolveFileUrl(m: ChatMessage): string {
    if (m.imageId) return this.getDocUrl(m.imageId);
    if (m.fileUrl) return this.getCleanFileUrl(m.fileUrl);
    return '';
  }

  private stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  }

  private doPoll(): void {
    if (this.loadingMsgs || this.sending) return;

    if (this.selectedUser) {
      this.http
        .get<any>(
          this.url(
            `/ctl/chat/history/private?withUserId=${encodeURIComponent(this.selectedUser.userId)}`,
          ),
        )
        .subscribe({
          next: (r) => this.mergeMessages(r?.result?.data ?? []),
          error: () => {},
        });
    } else if (this.selectedRoom) {
      this.http
        .get<any>(
          this.url(
            `/ctl/chat/history/group?roomId=${encodeURIComponent(this.selectedRoom.roomId)}`,
          ),
        )
        .subscribe({
          next: (r) => this.mergeMessages(r?.result?.data ?? []),
          error: () => {},
        });
    }
  }

  /**
   * Smart merge — koi duplicate nahi, sirf upgrade:
   * - Naya message → push karo, sort karo, scroll karo
   * - Existing message → status sirf upgrade karo (SENT→DELIVERED→SEEN, kabhi downgrade nahi)
   */
  private mergeMessages(fresh: ChatMessage[]): void {
    if (!fresh.length) return;
    let added = false;

    for (const inc of fresh) {
      const existing = this.messages.find(
        (m) =>
          m.timestamp === inc.timestamp &&
          m.fromEmailId === inc.fromEmailId &&
          m.content === inc.content,
      );

      if (!existing) {
        this.messages.push(inc);
        added = true;
      } else if (this.statusRank(inc.status) > this.statusRank(existing.status))
        existing.status = inc.status;
    }

    if (added) {
      this.messages.sort((a, b) => a.timestamp - b.timestamp);
      this.filterMessages();
      setTimeout(() => this.scrollBottom(), 50);
      if (this.selectedUser) {
        const hasNew = fresh.some(
          (m) => m.fromEmailId === this.selectedUser!.userId,
        );
        if (hasNew) this.markSeenFor(this.selectedUser.userId);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Tab
  // ─────────────────────────────────────────────────────────────────────────
  setTab(t: Tab): void {
    this.activeTab = t;
    this.searchQuery = '';
    t === 'private' ? this.applyUserSearch() : this.applyRoomSearch();
    if (t !== 'group') this.clearGroupSearch();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Login pe DELIVERED
  // ─────────────────────────────────────────────────────────────────────────
  private markAllDeliveredOnLogin(): void {
    this.http
      .post<any>(this.url('/ctl/chat/mark/delivered'), {})
      .subscribe({ next: () => {}, error: () => {} });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Users
  // ─────────────────────────────────────────────────────────────────────────
  loadUsers(): void {
    this.loadingUsers = true;
    this.http.get<any>(this.url('/ctl/chat/users')).subscribe({
      next: (r) => {
        this.users = (r?.result?.data ?? []).map((u: any) => ({
          userId: u.userId ?? '',
          displayName: u.displayName ?? '',
          email: u.email ?? '',
          online: u.online ?? false,
          lastTime: u.lastTime ?? 0,
          lastMessage: u.lastMessage ?? '',
          unreadCount: u.unreadCount ?? 0, // ✅ NEW — server se aayega ab
        }));
        this.applyUserSearch();
        this.loadingUsers = false;
        // ── Restore active private chat after users are ready ─────────
        this.tryRestoreActiveChat();
      },
      error: () => {
        this.loadingUsers = false;
      },
    });
  }

  applyUserSearch(): void {
    const q = this.searchQuery.trim().toLowerCase();
    let list = q
      ? this.users.filter(
          (u) =>
            u.displayName.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q),
        )
      : [...this.users];
    list.sort((a, b) => (b.lastTime ?? 0) - (a.lastTime ?? 0));
    this.filteredUsers = list;
  }

  selectUser(u: ChatUser): void {
    if (this.chatKey)
      this.replyDraftMap.set(this.chatKey, this.replyingTo ?? null);
    this.selectedUser = u;
    this.selectedRoom = undefined;
    u.unreadCount = 0;
    this.messages = [];
    this.loadingMsgs = true;
    // this.cancelReply();
    if (this.searchActive) {
      this.searchActive = false;
      this.searchMsgQuery = '';
    }
    this.clearGroupSearch();
    if (this.chatKey) {
      this.replyDraftMap.set(this.chatKey, this.replyingTo ?? null);
    }

    this.selectedUser = u;
    this.selectedRoom = undefined;
    u.unreadCount = 0;
    this.messages = [];
    this.loadingMsgs = true;

    // ── Persist active chat so page refresh restores it ───────────────
    this.saveActiveChat('private', u.userId);

    this.http
      .get<any>(
        this.url(
          `/ctl/chat/history/private?withUserId=${encodeURIComponent(u.userId)}`,
        ),
      )
      .subscribe({
        next: (r) => {
          this.messages = r?.result?.data ?? [];
          this.loadingMsgs = false;
          this.filterMessages();
          setTimeout(() => this.scrollBottom(), 50);
          this.markSeenFor(u.userId);
        },
        error: () => {
          this.loadingMsgs = false;
        },
      });

    this.sidebarOpen = false;
    if (this.checkIsMobile()) {
      this.showMobileChat = true;
    }
  }

  // Chat kholne par doosre user ke messages SEEN mark karo
  // Server Alice ko WS se STATUS_UPDATE SEEN bhejega → uske ticks blue
  private markSeenFor(withUserId: string): void {
    this.http
      .post<any>(this.url('/ctl/chat/mark/seen'), { withUserId })
      .subscribe({
        next: (r) => {
          if ((r?.result?.data ?? 0) > 0) {
            this.messages.forEach((m) => {
              if (m.fromEmailId === withUserId && m.status !== 'SEEN')
                m.status = 'SEEN';
            });
          }
        },
        error: () => {},
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Rooms
  // ─────────────────────────────────────────────────────────────────────────
  loadRooms(): void {
    this.loadingRooms = true;
    this.http.get<any>(this.url('/ctl/chat/room/list')).subscribe({
      next: (r) => {
        this.rooms = (r?.result?.data ?? []).map((rm: any) => ({
          roomId: rm.roomId ?? '',
          roomName: rm.roomName ?? rm.roomId ?? '',
          createdBy: rm.createdBy,
          memberUserIds: rm.memberUserIds ?? [],
          memberDetails: (rm.memberDetails ?? []) as MemberDetail[],
          lastTime: rm.lastTime ?? 0,
          lastMessage: rm.lastMessage ?? '',
        }));
        this.applyRoomSearch();
        this.loadingRooms = false;
        // WS connected ho to rooms subscribe karo, warna onConnect mein hoga
        if (this.wsConnected) {
          this.rooms.forEach((rm) =>
            this.subscribeRoomOnClient(this.stompClient!, rm.roomId),
          );
        }
        // ── Restore active group chat after rooms are ready ───────────
        this.tryRestoreActiveChat();
      },
      error: () => {
        this.loadingRooms = false;
      },
    });
  }

  applyRoomSearch(): void {
    const q = this.searchQuery.trim().toLowerCase();
    let list = q
      ? this.rooms.filter(
          (r) =>
            r.roomName.toLowerCase().includes(q) ||
            r.roomId.toLowerCase().includes(q),
        )
      : [...this.rooms];
    list.sort((a, b) => (b.lastTime ?? 0) - (a.lastTime ?? 0));
    this.filteredRooms = list;
  }

  selectRoom(rm: ChatRoom): void {
    this.selectedRoom = rm;
    this.selectedUser = undefined;
    rm.unreadCount = 0;
    this.messages = [];
    this.loadingMsgs = true;
    // this.cancelReply();
    if (this.chatKey) {
      this.replyDraftMap.set(this.chatKey, this.replyingTo ?? null);
    }

    this.selectedRoom = rm;
    this.selectedUser = undefined;
    rm.unreadCount = 0;
    this.messages = [];
    this.loadingMsgs = true;
    if (this.searchActive) {
      this.searchActive = false;
      this.searchMsgQuery = '';
    }
    this.clearGroupSearch();

    // ── Persist active chat so page refresh restores it ───────────────
    this.saveActiveChat('group', rm.roomId);

    this.http
      .get<any>(
        this.url(
          `/ctl/chat/history/group?roomId=${encodeURIComponent(rm.roomId)}`,
        ),
      )
      .subscribe({
        next: (r) => {
          // Normalise: SYSTEM type messages must carry isSystemMessage=true
          // so the template renders them as centre-aligned info pills.
          this.messages = (r?.result?.data ?? []).map((msg: ChatMessage) => {
            if (msg.type === 'SYSTEM') msg.isSystemMessage = true;
            return msg;
          });
          this.loadingMsgs = false;
          this.filterMessages();
          setTimeout(() => this.scrollBottom(), 50);
        },
        error: () => {
          this.loadingMsgs = false;
        },
      });

    this.sidebarOpen = false;
    if (this.checkIsMobile()) {
      this.showMobileChat = true;
    }
  }

  createRoom(): void {
    // ── Client-side validation ─────────────────────────────────────────
    const rawId = this.newRoomId.trim();
    const rawName = (this.newRoomName.trim() || rawId).trim();

    if (!rawId) {
      this.createRoomError = 'Group ID is required.';
      return;
    }
    if (
      !/^[a-z0-9][a-z0-9\-]*$/.test(rawId.toLowerCase().replace(/\s+/g, '-'))
    ) {
      this.createRoomError =
        'Group ID may only contain letters, numbers and hyphens.';
      return;
    }

    // Normalise the same way the backend does
    const sanitisedId = rawId.toLowerCase().replace(/\s+/g, '-');
    const sanitisedName = rawName.toLowerCase();
    if (this.rooms.some((r) => r.roomId.toLowerCase() === sanitisedId)) {
      this.createRoomError = `A group with ID "${sanitisedId}" already exists.`;
      return;
    }
    if (
      this.rooms.some((r) => r.roomName.trim().toLowerCase() === sanitisedName)
    ) {
      this.createRoomError = `A group named "${rawName}" already exists.`;
      return;
    }

    // ── Clear any previous error and call the server ──────────────────
    this.createRoomError = '';

    this.http
      .post<any>(this.url('/ctl/chat/room/create'), {
        roomId: rawId,
        roomName: rawName,
      })
      .subscribe({
        next: (r) => {
          if (r?.success) {
            this.newRoomId = '';
            this.newRoomName = '';
            this.createRoomError = '';
            this.showCreate = false;
            this.loadRooms();
          } else {
            // Server returned a business-logic error (race condition, etc.)
            this.createRoomError =
              r?.message ?? 'Could not create group. Please try again.';
          }
        },
        error: (err) => {
          this.createRoomError =
            err?.error?.message ?? 'Server error. Please try again.';
        },
      });
  }

  closeCreateForm(): void {
    this.showCreate = false;
    this.newRoomId = '';
    this.newRoomName = '';
    this.createRoomError = '';
  }

  joinRoom(): void {
    const input = this.joinRoomInput.trim();
    if (!input) {
      this.joinRoomError = 'Please enter a group ID or name.';
      return;
    }
    this.joinRoomError = '';
    this.joiningRoom = true;

    this.resolveRoomId(input).subscribe({
      next: (roomId) => {
        if (!roomId) {
          this.joinRoomError = `No group found matching "${input}".`;
          this.joiningRoom = false;
          return;
        }

        // ✅ Duplicate membership check
        const alreadyJoined = this.rooms.some((room) => room.roomId === roomId);
        if (alreadyJoined) {
          this.joinRoomError = 'You are already a member of this group.';
          this.joiningRoom = false;
          return;
        }

        this.http
          .post<any>(this.url('/ctl/chat/room/join'), { roomId })
          .subscribe({
            next: (r) => {
              this.joiningRoom = false;
              if (r?.success) {
                this.joinRoomInput = '';
                this.showJoin = false;
                this.loadRooms();
                setTimeout(() => {
                  const joinedRoom = this.rooms.find(
                    (rm) => rm.roomId === roomId,
                  );
                  if (joinedRoom) this.selectRoom(joinedRoom);
                }, 500);
              } else {
                this.joinRoomError = r?.message || 'Could not join group.';
              }
            },
            error: (err) => {
              this.joiningRoom = false;
              this.joinRoomError =
                err?.error?.message || 'Server error. Please try again.';
            },
          });
      },
      error: () => {
        this.joiningRoom = false;
        this.joinRoomError = 'Failed to search for group. Please try again.';
      },
    });
  }

  private resolveRoomId(input: string): Observable<string | null> {
    const trimmed = input.trim();
    if (!trimmed) return of(null);

    return this.http
      .get<any>(
        this.url(`/ctl/chat/room/search?q=${encodeURIComponent(trimmed)}`),
      )
      .pipe(
        map((res) => {
          const results = res?.result?.data ?? [];
          if (results.length === 0) return null;
          const lowerInput = trimmed.toLowerCase();
          const exactIdMatch = results.find(
            (r: any) => r.roomId?.toLowerCase() === lowerInput,
          );
          if (exactIdMatch) return exactIdMatch.roomId;
          const exactNameMatch = results.find(
            (r: any) => r.roomName?.toLowerCase() === lowerInput,
          );
          return exactNameMatch ? exactNameMatch.roomId : null;
        }),
      );
  }
  //  ADD MEMBER FEATURE  ★ NEW ★
  // ════════════════════════════════════════════════════════════════════════

  private initAddMemberSearchPipeline(): void {
    this.addMemberSearch$
      .pipe(
        debounceTime(280),
        distinctUntilChanged(),
        switchMap((query) => {
          const q = query.trim();
          const roomId = this.selectedRoom?.roomId;
          if (!roomId) {
            this.addMemberSuggestions = [];
            this.loadingAddMemberSuggestions = false;
            return of([] as AddMemberCandidate[]);
          }
          this.loadingAddMemberSuggestions = true;
          return this.fetchMemberSuggestions(roomId, q);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (results) => {
          this.loadingAddMemberSuggestions = false;
          this.addMemberSuggestions = results;
        },
        error: () => {
          this.loadingAddMemberSuggestions = false;
        },
      });
  }

  /** Calls the backend suggestion endpoint. Returns an Observable. */
  private fetchMemberSuggestions(
    roomId: string,
    query: string,
  ): Observable<AddMemberCandidate[]> {
    const encoded = encodeURIComponent(query);
    const url = this.url(
      `/ctl/chat/room/suggest-users?roomId=${encodeURIComponent(roomId)}&name=${encoded}`,
    );
    return new Observable<AddMemberCandidate[]>((obs) => {
      this.http.get<any>(url).subscribe({
        next: (r) => {
          obs.next(r?.result?.data ?? []);
          obs.complete();
        },
        error: () => {
          obs.next([]);
          obs.complete();
        },
      });
    });
  }

  /** Opens the Add Member modal and resets its state. */
  openAddMemberModal(): void {
    this.showAddMemberModal = true;
    this.addMemberSearchQuery = '';
    this.addMemberSuggestions = [];
    this.selectedAddMemberUser = null;
    this.addMemberMessage = '';
    this.addMemberError = false;
    this.addingMember = false;
    // Trigger an empty query to pre-load suggestions (shows first 20 users)
    setTimeout(() => this.addMemberSearch$.next(''), 50);
  }

  /** Closes the modal and clears all add-member state. */
  closeAddMemberModal(): void {
    this.showAddMemberModal = false;
    this.addMemberSearchQuery = '';
    this.addMemberSuggestions = [];
    this.selectedAddMemberUser = null;
    this.addMemberMessage = '';
    this.addMemberError = false;
  }

  /** Called on every keystroke in the add-member search box. */
  onAddMemberSearchInput(value: string): void {
    this.addMemberSearchQuery = value;
    this.selectedAddMemberUser = null; // clear previous selection when typing
    this.addMemberMessage = '';
    this.addMemberSearch$.next(value);
  }

  /** User clicks a suggestion row. */
  selectAddMemberCandidate(candidate: AddMemberCandidate): void {
    this.selectedAddMemberUser = candidate;
    this.addMemberSearchQuery = candidate.displayName;
    this.addMemberSuggestions = []; // hide dropdown once selected
    this.addMemberMessage = '';
  }

  /**
   * Confirms the add-member action.
   * Calls POST /ctl/chat/room/add-member
   * Backend validates: same app+org, not already a member.
   */
  confirmAddMember(): void {
    const roomId = this.selectedRoom?.roomId;
    const target = this.selectedAddMemberUser;
    if (!roomId || !target || this.addingMember) return;

    this.addingMember = true;
    this.addMemberMessage = '';

    this.http
      .post<any>(this.url('/ctl/chat/room/add-member'), {
        roomId: roomId,
        userId: target.userId,
      })
      .subscribe({
        next: (r) => {
          this.addingMember = false;
          if (r?.success) {
            this.addMemberMessage = `${target.displayName} was added to the group.`;
            this.addMemberError = false;
            this.selectedAddMemberUser = null;
            this.addMemberSearchQuery = '';
            // Refresh suggestions (now excludes the newly-added user)
            setTimeout(() => this.addMemberSearch$.next(''), 100);
            // Optimistically update local room state (WS will also broadcast)
            const memberDetails: MemberDetail[] = r?.result?.data ?? [];
            if (memberDetails.length)
              this.applyMemberUpdate(roomId, memberDetails);
          } else {
            this.addMemberMessage = r?.message ?? 'Could not add member.';
            this.addMemberError = true;
          }
        },
        error: (err) => {
          this.addingMember = false;
          this.addMemberMessage =
            err?.error?.message ?? 'Server error. Please try again.';
          this.addMemberError = true;
        },
      });
  }
  // ════════════════════════════════════════════════════════════════════════
  // GROUP SEARCH  ★ NEW IMPLEMENTATION ★
  // ════════════════════════════════════════════════════════════════════════

  /**
   * Boot the RxJS pipeline once.  All typing events funnel through here so
   * debounce, dedup and cancellation are handled automatically.
   */
  private initGroupSearchPipeline(): void {
    this.groupSearch$
      .pipe(
        debounceTime(250), // wait 250ms after last keystroke
        distinctUntilChanged(), // skip if same query
        switchMap((query) => {
          const q = query.trim();
          if (q.length < 1) {
            this.groupSuggestions = [];
            this.showGroupSuggestions = false;
            this.groupSearchEmpty = false;
            this.searchingGroups = false;
            return of([] as GroupSuggestion[]);
          }
          this.searchingGroups = true;
          return this.performGroupSearch(q);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (results: GroupSuggestion[]) => {
          this.searchingGroups = false;
          this.groupSuggestions = results;
          this.groupSearchEmpty = results.length === 0;
          this.showGroupSuggestions =
            results.length > 0 || this.groupSearchQuery.trim().length > 0;
          this.activeSuggestionIndex = -1;
        },
        error: () => {
          this.searchingGroups = false;
        },
      });
  }

  /**
   * Search against:
   *   1. Already-loaded `rooms[]` (instant, client-side)
   *   2. Server endpoint `/ctl/chat/room/search?q=...` for rooms the user
   *      has NOT yet joined (discover-mode).
   *
   * Results are merged, de-duped and ranked by match score.
   */
  private performGroupSearch(query: string): Observable<GroupSuggestion[]> {
    const lower = query.toLowerCase();

    // ── Client-side: joined rooms ──────────────────────────────────────
    const clientResults: GroupSuggestion[] = this.rooms
      .filter(
        (r) =>
          r.roomName.toLowerCase().includes(lower) ||
          r.roomId.toLowerCase().includes(lower),
      )
      .map((r) => ({
        roomId: r.roomId,
        roomName: r.roomName,
        memberCount: r.memberDetails?.length ?? r.memberUserIds?.length ?? 0,
        memberDetails: r.memberDetails,
        lastMessage: r.lastMessage,
        lastTime: r.lastTime,
        isJoined: true,
        createdBy: r.createdBy,
        matchScore: this.calcMatchScore(r.roomName, r.roomId, lower),
      }));

    // ── Server-side: all visible rooms (discovery) ─────────────────────
    return new Observable<GroupSuggestion[]>((observer) => {
      this.http
        .get<any>(
          this.url(`/ctl/chat/room/search?q=${encodeURIComponent(query)}`),
        )
        .subscribe({
          next: (res) => {
            const serverRooms: any[] = res?.result?.data ?? [];
            const joinedIds = new Set(this.rooms.map((r) => r.roomId));

            const serverResults: GroupSuggestion[] = serverRooms
              .filter((r) => r.roomId)
              .map((r) => ({
                roomId: r.roomId,
                roomName: r.roomName ?? r.roomId,
                memberCount: r.memberCount ?? r.memberUserIds?.length ?? 0,
                isJoined: joinedIds.has(r.roomId),
                createdBy: r.createdBy,
                matchScore: this.calcMatchScore(
                  r.roomName ?? r.roomId,
                  r.roomId,
                  lower,
                ),
              }));

            // Merge: client results first (already joined), then extras from server
            const merged = [...clientResults];
            for (const sr of serverResults) {
              if (!merged.find((m) => m.roomId === sr.roomId)) merged.push(sr);
            }

            merged.sort(
              (a, b) =>
                (b.matchScore ?? 0) - (a.matchScore ?? 0) ||
                a.roomName.localeCompare(b.roomName),
            );

            observer.next(merged.slice(0, 12));
            observer.complete();
          },
          error: () => {
            // Server unavailable — fall back to client-only results
            clientResults.sort(
              (a, b) =>
                (b.matchScore ?? 0) - (a.matchScore ?? 0) ||
                a.roomName.localeCompare(b.roomName),
            );
            observer.next(clientResults.slice(0, 12));
            observer.complete();
          },
        });
    });
  }

  /**
   * Score a room's relevance to the search term.
   * Exact name match = highest, starts-with = high, includes = normal.
   */
  private calcMatchScore(name: string, id: string, query: string): number {
    const n = name.toLowerCase();
    const q = query.toLowerCase();
    if (n === q) return 100;
    if (n.startsWith(q)) return 80;
    if (id.toLowerCase() === q) return 70;
    if (n.includes(q)) return 50;
    if (id.toLowerCase().includes(q)) return 30;
    return 0;
  }

  /** Called on every keystroke in the group search input. */
  onGroupSearchInput(value: string): void {
    this.groupSearchQuery = value;
    if (!value.trim()) {
      this.clearGroupSearch();
      // When search is cleared, restore the full list
      this.applyRoomSearch();
    } else {
      // Also apply instant filter on the sidebar list while user types
      this.applyRoomSearchWithQuery(value);
    }
    this.groupSearch$.next(value);
  }

  /** Filter the sidebar list using the current group search query. */
  private applyRoomSearchWithQuery(query: string): void {
    const q = query.trim().toLowerCase();
    let list = q
      ? this.rooms.filter(
          (r) =>
            r.roomName.toLowerCase().includes(q) ||
            r.roomId.toLowerCase().includes(q),
        )
      : [...this.rooms];
    list.sort((a, b) => (b.lastTime ?? 0) - (a.lastTime ?? 0));
    this.filteredRooms = list;
  }

  /** Keyboard navigation within the suggestion dropdown. */
  onGroupSearchKeydown(event: KeyboardEvent): void {
    if (!this.showGroupSuggestions || !this.groupSuggestions.length) {
      if (event.key === 'Escape') this.clearGroupSearch();
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeSuggestionIndex = Math.min(
          this.activeSuggestionIndex + 1,
          this.groupSuggestions.length - 1,
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.activeSuggestionIndex = Math.max(
          this.activeSuggestionIndex - 1,
          0,
        );
        break;

      case 'Enter':
        event.preventDefault();
        if (this.activeSuggestionIndex >= 0) {
          this.selectSuggestion(
            this.groupSuggestions[this.activeSuggestionIndex],
          );
        }
        break;

      case 'Escape':
        this.clearGroupSearch();
        break;
    }
  }

  /**
   * User clicks or presses Enter on a suggestion.
   * • If already joined → open the room immediately.
   * • If not joined     → join then open.
   */
  selectSuggestion(suggestion: GroupSuggestion): void {
    this.clearGroupSearch();

    if (suggestion.isJoined) {
      const room = this.rooms.find((r) => r.roomId === suggestion.roomId);
      if (room) {
        this.selectRoom(room);
        return;
      }
    }

    // Not joined yet — join then refresh
    this.http
      .post<any>(this.url('/ctl/chat/room/join'), { roomId: suggestion.roomId })
      .subscribe({
        next: (r) => {
          this.loadRooms();
          if (r?.success) {
            // After loadRooms finishes, select the room
            setTimeout(() => {
              const joined = this.rooms.find(
                (rm) => rm.roomId === suggestion.roomId,
              );
              if (joined) this.selectRoom(joined);
            }, 600);
          }
        },
        error: () => {},
      });
  }

  /** Dismiss dropdown and reset all search state. */
  clearGroupSearch(): void {
    this.groupSearchQuery = '';
    this.groupSuggestions = [];
    this.showGroupSuggestions = false;
    this.groupSearchEmpty = false;
    this.activeSuggestionIndex = -1;
    this.searchingGroups = false;
    this.hoveredSuggestion = null;
  }

  /** Returns the part of the name that matched (for highlight rendering). */
  highlightMatch(
    text: string,
    query: string,
  ): { before: string; match: string; after: string } {
    if (!query.trim()) return { before: text, match: '', after: '' };
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return { before: text, match: '', after: '' };
    return {
      before: text.slice(0, idx),
      match: text.slice(idx, idx + query.length),
      after: text.slice(idx + query.length),
    };
  }

  /** Format the member count label shown in suggestions. */
  memberCountLabel(count: number): string {
    return count === 0
      ? 'No members'
      : `${count} member${count !== 1 ? 's' : ''}`;
  }

  /** Whether a suggestion is the currently keyboard-active one. */
  isSuggestionActive(i: number): boolean {
    return this.activeSuggestionIndex === i;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Send
  // ─────────────────────────────────────────────────────────────────────────
  sendMessage(): void {
    if (this.sending) return;
    if (this.pendingFile) {
      this.sendPendingFile();
    } else if (this.newMessage.trim()) {
      if (this.selectedUser) this.sendPrivate(this.selectedUser);
      else if (this.selectedRoom) this.sendGroup(this.selectedRoom);
    }
  }
  async sendPendingFile(): Promise<void> {
    if (!this.pendingFile) return;
    if (!this.selectedUser && !this.selectedRoom) return;

    const file = this.pendingFile;
    const replyTo = this.replyingTo
      ? this.buildReplyContext(this.replyingTo)
      : null;
    this.pendingFile = null;
    this.pendingFileError = null;
    this.pendingFilePreviewUrl = null;
    this.replyingTo = null;
    this.sending = true;

    try {
      const form = new FormData();
      form.append('file', file, file.name);
      if (this.selectedUser) form.append('toEmailId', this.selectedUser.userId);
      else if (this.selectedRoom)
        form.append('roomId', this.selectedRoom.roomId);
      form.append('content', this.newMessage.trim());
      this.newMessage = '';
      if (replyTo) {
        if (replyTo.id) form.append('replyToId', replyTo.id);
        if (replyTo.fromEmailId)
          form.append('replyToFromEmailId', replyTo.fromEmailId);
        if (replyTo.fromName) form.append('replyToFromName', replyTo.fromName);
        if (replyTo.content) form.append('replyToContent', replyTo.content);
        if (replyTo.fileOriginalName)
          form.append('replyToFileOriginalName', replyTo.fileOriginalName);
        if (replyTo.fileType) form.append('replyToFileType', replyTo.fileType);
      }
      form.append('content', this.newMessage.trim());
      this.newMessage = '';

      const endpoint = this.selectedUser
        ? '/ctl/chat/send/private/file'
        : '/ctl/chat/send/group/file';

      const res = await this.http
        .post<any>(this.url(endpoint), form)
        .toPromise();

      if (!res?.success) throw new Error(res?.message ?? 'Upload failed');

      const finalMsg: ChatMessage = res.result?.data;
      if (!finalMsg) throw new Error('No message data in response');

      this.messages.push({
        ...finalMsg,
        fromEmailId: finalMsg.fromEmailId ?? this.me,
        fromName: finalMsg.fromName ?? this.myName,
        content: finalMsg.content ?? '',
        type: finalMsg.type ?? (this.selectedRoom ? 'GROUP' : 'PRIVATE'),
        timestamp: finalMsg.timestamp ?? Date.now(),
        status: finalMsg.status ?? 'SENT',
        imageId: finalMsg.imageId,
        fileOriginalName: finalMsg.fileOriginalName ?? file.name,
        fileSize: finalMsg.fileSize ?? file.size,
        fileType: finalMsg.fileType,
        isImages: finalMsg.isImages ?? false,
        replyTo: finalMsg.replyTo,
      });

      this.filterMessages();
      setTimeout(() => this.scrollBottom(), 50);

      const preview = `📎 ${file.name}`;
      if (this.selectedUser) {
        const f = this.users.find(
          (x) => x.userId === this.selectedUser!.userId,
        );
        if (f) {
          f.lastMessage = preview;
          f.lastTime = finalMsg.timestamp;
        }
        this.applyUserSearch();
      } else if (this.selectedRoom) {
        const f = this.rooms.find(
          (x) => x.roomId === this.selectedRoom!.roomId,
        );
        if (f) {
          f.lastMessage = preview;
          f.lastTime = finalMsg.timestamp;
        }
        this.applyRoomSearch();
      }
    } catch (err: any) {
      console.error('[FileUpload]', err);
      alert('File send failed: ' + (err?.message ?? err));
    } finally {
      this.sending = false;
    }
  }

  private sendPrivate(user: ChatUser): void {
    const content = this.newMessage.trim();
    const replyTo = this.replyingTo
      ? this.buildReplyContext(this.replyingTo)
      : undefined;
    this.replyingTo = null;
    this.sending = true;
    const payload: any = { toEmailId: user.userId, content };
    if (replyTo) payload.replyTo = replyTo;
    this.http.post<any>(this.url('/ctl/chat/send/private'), payload).subscribe({
      next: (r) => {
        const saved = r?.result?.data ?? {};
        this.messages.push({
          fromEmailId: this.me,
          toEmailId: user.userId,
          content: saved.content ?? content,
          type: 'PRIVATE',
          timestamp: saved.timestamp ?? Date.now(),
          // Server se status lo — SENT ya DELIVERED (based on online check)
          status: saved.status ?? 'SENT',
          _id: saved._id,
          id: saved.id,
          replyTo: saved.replyTo,
        });
        this.newMessage = '';
        this.sending = false;
        this.filterMessages();
        setTimeout(() => this.scrollBottom(), 50);
        const f = this.users.find((x) => x.userId === user.userId);
        if (f) {
          f.lastMessage = content;
          f.lastTime = Date.now();
        }
        this.applyUserSearch();
      },
      error: () => {
        this.sending = false;
      },
    });
  }

  private sendGroup(room: ChatRoom): void {
    const content = this.newMessage.trim();
    const replyTo = this.replyingTo
      ? this.buildReplyContext(this.replyingTo)
      : undefined;
    this.replyingTo = null;
    this.sending = true;
    const payload: any = { roomId: room.roomId, content };
    if (replyTo) payload.replyTo = replyTo;
    this.http.post<any>(this.url('/ctl/chat/send/group'), payload).subscribe({
      next: (r) => {
        const saved = r?.result?.data ?? {};
        this.messages.push({
          fromEmailId: this.me,
          fromName: this.myName,
          roomId: room.roomId,
          content: saved.content ?? content,
          type: 'GROUP',
          timestamp: saved.timestamp ?? Date.now(),
          status: saved.status ?? 'SENT',
          id: saved.id,
          replyTo: saved.replyTo,
        });
        this.newMessage = '';
        this.sending = false;
        this.filterMessages();
        setTimeout(() => this.scrollBottom(), 50);
        const f = this.rooms.find((x) => x.roomId === room.roomId);
        if (f) {
          f.lastMessage = content;
          f.lastTime = Date.now();
        }
        this.applyRoomSearch();
      },
      error: () => {
        this.sending = false;
      },
    });
  }

  onEnter(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  autoResize(event: Event): void {
    const ta = event.target as HTMLTextAreaElement;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }

  // ─────────────────────────────────────────────────────────────────────────
  // File upload — SINGLE multipart call to /send/private/file or
  //               /send/group/file
  //
  // The backend handles ALL steps:
  //   1. Save message document
  //   2. Upload file to doc service (original bytes, no re-encoding)
  //   3. Update document with imageId, fileOriginalName, fileSize,
  //      fileType, isImages=true
  //   4. Forward via WebSocket
  //   5. Return the final document
  //
  // Angular sends: FormData { toEmailId|roomId, content, file }
  // ─────────────────────────────────────────────────────────────────────────
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    // 2 MB client-side validation
    if (file.size > 2 * 1024 * 1024) {
      this.pendingFileError = 'File size must be less than 2 MB';
      this.pendingFile = null;
      return;
    }

    this.pendingFileError = null;
    this.pendingFile = file;
    if (file.type.startsWith('image/')) {
      this.pendingFilePreviewUrl = URL.createObjectURL(file);
    } else {
      this.pendingFilePreviewUrl = null;
    }
  }

  clearPendingFile(): void {
    this.pendingFile = null;
    this.pendingFileError = null;
    this.pendingFilePreviewUrl = null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Open / preview file
  // ─────────────────────────────────────────────────────────────────────────
  // openFile(m: ChatMessage): void {
  //   const url = this.resolveFileUrl(m);
  //   if (!url) return;
  //   if (m.fileType === 'IMAGE') {
  //     this.http
  //       .get(url, { responseType: 'blob', withCredentials: true })
  //       .subscribe({
  //         next: (blob) => {
  //           this.previewImageUrl = URL.createObjectURL(blob);
  //         },
  //         error: () => alert('Image could not be opened'),
  //       });
  //   } else {
  //     this.http
  //       .get(url, { responseType: 'blob', withCredentials: true })
  //       .subscribe({
  //         next: (blob) => {
  //           const blobUrl = URL.createObjectURL(blob);
  //           const a = document.createElement('a');
  //           a.href = blobUrl;
  //           a.download = m.fileOriginalName ?? m.fileName ?? 'download';
  //           document.body.appendChild(a);
  //           a.click();
  //           document.body.removeChild(a);
  //           URL.revokeObjectURL(blobUrl);
  //         },
  //         error: () => alert('File could not be downloaded'),
  //       });
  //   }
  // }
  openFile(m: any): void {
    const url = m.imageId ? this.getDocUrl(m.imageId) : this.resolveFileUrl(m);

    if (!url) return;
    if (this.isImageFile(m)) this.previewImageUrl = url;
    else this.downloadFile(url, m.fileOriginalName || 'file');
  }

  downloadFile(url: string, fileName: string): void {
    fetch(url, { mode: 'cors' })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        // Fallback: naye tab mein open karo
        window.open(url, '_blank');
      });
  }

  closePreview(): void {
    if (this.previewImageUrl) {
      URL.revokeObjectURL(this.previewImageUrl);
      this.previewImageUrl = null;
    }
  }

  private getCleanFileUrl(fileUrl: string): string {
    let p = fileUrl.split(';')[0];
    if (!p.startsWith('/')) p = '/' + p;
    return `${this.QD}${p}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // WebSocket
  // KEY FIX: Angular subscribe karta hai /user/queue/messages
  // Spring internally map karta hai: /user/{principal}/queue/messages
  // Principal = fromUserId header jo CONNECT mein bheja = this.me (email)
  // ─────────────────────────────────────────────────────────────────────────
  private connectWS(): void {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${this.CHAT}/ws-chat`),
      connectHeaders: { fromUserId: this.me },
      reconnectDelay: 15000,
      debug: () => {},

      onConnect: () => {
        this.wsConnected = true;
        console.log('[WS] Connected as:', this.me);

        // ── PRIVATE messages + STATUS_UPDATE ────────────────────────
        // FIX: /user/queue/messages (NOT /user/${this.me}/queue/messages)
        // Spring automatically routes /user/{principal}/queue/messages
        // jab hum /user/queue/messages subscribe karte hain
        client.subscribe('/user/queue/messages', (frame: IMessage) => {
          const raw = JSON.parse(frame.body);

          // STATUS_UPDATE — tick change (DELIVERED ya SEEN)
          if (raw.type === 'STATUS_UPDATE') {
            const evt = raw as StatusUpdateEvent;
            // toEmailId = main hoon (Alice) — mere messages ke ticks update karo
            if (evt.toEmailId === this.me) {
              this.messages.forEach((m) => {
                if (
                  m.fromEmailId === this.me &&
                  m.toEmailId === evt.fromEmailId &&
                  this.statusRank(m.status) < this.statusRank(evt.subType)
                )
                  m.status = evt.subType;
              });
            }
            return;
          }
          if (raw.type === 'ROOM_ADDED') {
            const exists = this.rooms.some((r) => r.roomId === raw.roomId);
            if (!exists) {
              const sysMsgFromPayload: ChatMessage | null = raw.systemMessage
                ? {
                    ...(raw.systemMessage as ChatMessage),
                    isSystemMessage: true,
                    type: 'SYSTEM',
                  }
                : null;
              const newRoom: ChatRoom = {
                roomId: raw.roomId,
                roomName: raw.roomName ?? raw.roomId,
                createdBy: raw.createdBy,
                memberUserIds: (raw.memberDetails ?? []).map(
                  (m: MemberDetail) => m.userId,
                ),
                memberDetails: raw.memberDetails ?? [],
                unreadCount: sysMsgFromPayload ? 1 : 0,
                lastMessage: sysMsgFromPayload?.content ?? '',
                lastTime:
                  sysMsgFromPayload?.timestamp ?? raw.timestamp ?? Date.now(),
              };
              this.rooms = [newRoom, ...this.rooms];
              this.applyRoomSearch();
              // Subscribe to the new room's WS topic immediately
              if (this.stompClient?.connected) {
                this.subscribeRoomOnClient(this.stompClient, raw.roomId);
              }
              // If the added user has this room open (edge case), show the pill
              if (
                sysMsgFromPayload &&
                this.selectedRoom?.roomId === raw.roomId
              ) {
                this.messages.push(sysMsgFromPayload);
                this.filterMessages();
                this.doScroll = true;
              }
            }
            return;
          }
          if (raw.type === 'ROOM_REMOVED') {
            this.handleRoomRemoved(raw.roomId);
            return;
          }
          // Regular private message
          const msg = raw as ChatMessage;
          if (msg.fromEmailId === this.me) return; // echo guard

          if (msg.type === 'PRIVATE') {
            if (this.selectedUser?.userId === msg.fromEmailId) {
              // Chat khuli hai — message turant dikhao, duplicate check karo
              const exists = this.messages.some(
                (m) =>
                  m.timestamp === msg.timestamp &&
                  m.fromEmailId === msg.fromEmailId,
              );
              if (!exists) {
                this.messages.push(msg);
                this.filterMessages();
                this.doScroll = true;
              }
              // Sender ko batao ki maine dekha — SEEN send karo
              this.markSeenFor(msg.fromEmailId);
            } else {
              // Chat nahi khuli — unread badge update karo
              const found = this.users.find(
                (x) => x.userId === msg.fromEmailId,
              );
              if (found) {
                found.unreadCount = (found.unreadCount ?? 0) + 1;
                found.lastMessage =
                  msg.content || (msg.imageId ? '📎 File' : '');
                found.lastTime = msg.timestamp;
              }
            }
            this.applyUserSearch();
          }
        });

        // ── Online / offline presence ────────────────────────────────
        client.subscribe('/topic/online', (frame: IMessage) => {
          try {
            const d: { userId: string; online: boolean } = JSON.parse(
              frame.body,
            );
            const found = this.users.find((x) => x.userId === d.userId);
            if (found) found.online = d.online;
            if (this.selectedUser?.userId === d.userId)
              this.selectedUser.online = d.online;
          } catch {}
        });

        // ── Group rooms subscribe ────────────────────────────────────
        // loadRooms async hai — jo rooms abhi loaded hain unhe subscribe karo
        this.rooms.forEach((rm) =>
          this.subscribeRoomOnClient(client, rm.roomId),
        );
      },

      onDisconnect: () => {
        this.wsConnected = false;
        this.roomSubs.clear();
        console.log('[WS] Disconnected — polling will cover until reconnect');
      },
      onStompError: () => {
        this.wsConnected = false;
      },
    });

    this.stompClient = client;
    client.activate();
  }

  private subscribeRoom(roomId: string): void {
    if (this.stompClient?.connected)
      this.subscribeRoomOnClient(this.stompClient, roomId);
  }

  // AFTER — fixed
  // private subscribeRoomOnClient(client: Client, roomId: string): void {
  //   if (this.roomSubs.has(roomId)) return;

  //   // Must match backend roomScopedKey(orgId, appId, roomId) = "orgId.appId.roomId"
  //   const scopedTopic = `/topic/room.${this.myOrgId}.${this.myAppId}.${roomId}`;

  //   const sub = client.subscribe(scopedTopic, (frame: IMessage) => {
  //     // FIX: wrap in ngZone.run() so Angular change detection fires
  //     this.ngZone.run(() => {
  //       const raw = JSON.parse(frame.body);

  //       if (raw.type === 'ROOM_MEMBER_UPDATE') {
  //         // FIX: raw.roomId is scopedKey; use closure var roomId (plain) for lookup
  //         this.applyMemberUpdate(roomId, raw.memberDetails);
  //         return;
  //       }

  //       const msg = raw as ChatMessage;

  //       // Deduplicate instead of unconditionally dropping own messages.
  //       // (Own messages are already pushed optimistically in sendGroup(),
  //       // so we just skip the duplicate — same as the existing check below.)
  //       const exists = this.messages.some(
  //         (m) =>
  //           m.timestamp === msg.timestamp && m.fromEmailId === msg.fromEmailId,
  //       );
  //       if (exists) return; // already rendered — skip regardless of sender

  //       if (this.selectedRoom?.roomId === roomId) {
  //         this.messages.push(msg);
  //         this.filterMessages();
  //         this.doScroll = true;
  //       } else {
  //         const found = this.rooms.find((x) => x.roomId === roomId);
  //         if (found) {
  //           found.unreadCount = (found.unreadCount ?? 0) + 1;
  //           found.lastMessage = msg.content || (msg.imageId ? '📎 File' : '');
  //           found.lastTime = msg.timestamp;
  //         }
  //       }
  //       this.applyRoomSearch();
  //     }); // end ngZone.run
  //   });

  //   this.roomSubs.set(roomId, sub);
  // }

  private subscribeRoomOnClient(client: Client, roomId: string): void {
    if (this.roomSubs.has(roomId)) return;

    const scopedTopic = `/topic/room.${this.myOrgId}.${this.myAppId}.${roomId}`;

    const sub = client.subscribe(scopedTopic, (frame: IMessage) => {
      this.ngZone.run(() => {
        const raw = JSON.parse(frame.body);

        if (raw.type === 'ROOM_MEMBER_UPDATE') {
          this.applyMemberUpdate(roomId, raw.memberDetails);
          return;
        }
        if (raw.type === 'ROOM_DELETED') {
          this.handleRoomRemoved(roomId);
          return;
        }

        const msg = raw as ChatMessage;
        if (msg.type === 'SYSTEM') {
          msg.isSystemMessage = true;
          if (this.selectedRoom?.roomId === roomId) {
            this.messages.push(msg);
            this.filterMessages();
            this.doScroll = true;
          } else {
            const found = this.rooms.find((x) => x.roomId === roomId);
            if (found) {
              found.lastMessage = msg.content;
              found.lastTime = msg.timestamp;
            }
          }
          this.applyRoomSearch();
          return;
        }
        // FIX 1: Echo guard – skip own messages (sender sees via HTTP response)
        if (msg.fromEmailId === this.me) return;

        // FIX 2: Deduplicate by unique ID (if ID exists)
        if (msg.id && this.messages.some((m) => m.id === msg.id)) return;

        // Fallback deduplication by timestamp+fromEmailId (only if ID missing – should not happen)
        const exists = this.messages.some(
          (m) =>
            m.timestamp === msg.timestamp && m.fromEmailId === msg.fromEmailId,
        );
        if (exists) return;

        if (this.selectedRoom?.roomId === roomId) {
          this.messages.push(msg);
          this.filterMessages();
          this.doScroll = true;
        } else {
          const found = this.rooms.find((x) => x.roomId === roomId);
          if (found) {
            found.unreadCount = (found.unreadCount ?? 0) + 1;
            found.lastMessage = msg.content || (msg.imageId ? '📎 File' : '');
            found.lastTime = msg.timestamp;
          }
        }
        this.applyRoomSearch();
      });
    });

    this.roomSubs.set(roomId, sub);
  }
  // ─────────────────────────────────────────────────────────────────────────
  // UI helpers
  // ─────────────────────────────────────────────────────────────────────────
  onSearchChange(): void {
    this.activeTab === 'private'
      ? this.applyUserSearch()
      : this.applyRoomSearch();
  }

  private scrollBottom(): void {
    if (!this.msgBox) return;
    try {
      this.msgBox.nativeElement.scrollTop =
        this.msgBox.nativeElement.scrollHeight;
    } catch {}
  }

  isMine(m: ChatMessage): boolean {
    return m.fromEmailId === this.me;
  }

  fmt(ts: number): string {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  initials(n = ''): string {
    return (
      n
        .split(' ')
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase()
        .slice(0, 2) || '?'
    );
  }

  color(id = ''): string {
    const p = [
      '#008cff',
      '#343a40',
      '#6f42c1',
      '#20c997',
      '#fd7e14',
      '#dc3545',
      '#0dcaf0',
      '#198754',
    ];
    let h = 0;
    for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
    return p[Math.abs(h) % p.length];
  }

  senderName(m: ChatMessage): string {
    return m.fromName ?? m.fromEmailId?.split('@')[0] ?? '';
  }

  // Status rank — kabhi downgrade nahi hoga
  // SENT=1  DELIVERED=2  SEEN=3
  statusRank(s?: string): number {
    if (s === 'SEEN') return 3;
    if (s === 'DELIVERED') return 2;
    return 1;
  }

  getDateLabel(ts: number): string {
    if (!ts) return '';
    const d = new Date(ts),
      today = new Date(),
      yest = new Date();
    yest.setDate(today.getDate() - 1);
    const same = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (same(d, today)) return 'Today';
    if (same(d, yest)) return 'Yesterday';
    return d.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  isNewDay(index: number): boolean {
    if (index === 0) return true;
    const prev = this.messages[index - 1],
      curr = this.messages[index];
    if (!prev?.timestamp || !curr?.timestamp) return false;
    const p = new Date(prev.timestamp),
      c = new Date(curr.timestamp);
    return (
      p.getFullYear() !== c.getFullYear() ||
      p.getMonth() !== c.getMonth() ||
      p.getDate() !== c.getDate()
    );
  }
  isCurrentMatch(index: number): boolean {
    return (
      this.matchesList.length > 0 &&
      this.matchesList[this.currentMatchIndex] === index
    );
  }

  // ── Message search ────────────────────────────────────────────────────
  filterMessages(): void {
    const query = this._searchMsgQuery.trim();
    if (query.length < 2) {
      this.matchesList = [];
      this.currentMatchIndex = -1;
      return;
    }
    const lower = query.toLowerCase();
    this.matchesList = [];
    this.messages.forEach((msg, idx) => {
      if (msg.content.toLowerCase().includes(lower)) this.matchesList.push(idx);
    });
    if (this.matchesList.length) {
      this.currentMatchIndex = 0;
      setTimeout(() => this.scrollToMatch(this.matchesList[0]), 50);
    } else {
      this.currentMatchIndex = -1;
    }
  }

  private applyHighlights(): void {
    if (!this.msgBox) return;
    const all: NodeListOf<HTMLElement> =
      this.msgBox.nativeElement.querySelectorAll('.msg-wrapper');
    all.forEach((el, i) => {
      el.classList.toggle('search-match', this.matchesList.includes(i));
      el.classList.remove('current-match');
    });
    if (this.currentMatchIndex >= 0) {
      const idx = this.matchesList[this.currentMatchIndex];
      if (all[idx]) all[idx].classList.add('current-match');
    }
  }

  scrollToMatch(index: number): void {
    const msg = this.messages[index];
    if (!msg) return;
    const msgId = this.msgKey(msg);
    const element = document.querySelector(
      `.msg-wrapper[data-msg-id="${msgId}"]`,
    ) as HTMLElement;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
  nextMatch(): void {
    if (!this.matchesList.length) return;
    this.currentMatchIndex =
      (this.currentMatchIndex + 1) % this.matchesList.length;
    this.scrollToMatch(this.matchesList[this.currentMatchIndex]);
  }
  prevMatch(): void {
    if (!this.matchesList.length) return;
    this.currentMatchIndex =
      (this.currentMatchIndex - 1 + this.matchesList.length) %
      this.matchesList.length;
    this.scrollToMatch(this.matchesList[this.currentMatchIndex]);
  }

  /** Mobile back button — returns to sidebar (user/group list) */
  goBackToSidebar(): void {
    this.showMobileChat = false;
    this.sidebarOpen = true;
    this.selectedUser = undefined;
    this.selectedRoom = undefined;
    this.messages = [];
    this.searchActive = false;
    this.showMembersPanel = false;
  }

  toggleSearchBox(): void {
    this.searchActive = !this.searchActive;
    if (!this.searchActive) {
      this.searchMsgQuery = '';
      this.filterMessages();
    } else
      setTimeout(
        () =>
          (
            document.querySelector('.msg-search-input') as HTMLInputElement
          )?.focus(),
        50,
      );
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event: any): void {
    this.newMessage += event.emoji.native;
    this.showEmojiPicker = false;
    setTimeout(() => {
      const ta = document.querySelector('.msg-textarea') as HTMLTextAreaElement;
      if (ta) {
        ta.focus();
        this.autoResize({ target: ta } as any);
      }
    }, 50);
  }

  isSearchMatch(msg: ChatMessage): boolean {
    const q = this.searchMsgQuery.trim();
    return !!q && msg.content.toLowerCase().includes(q.toLowerCase());
  }

  imgErr(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  fileIconColor(fileType: string | undefined): string {
    const type = (fileType || '').toLowerCase();
    if (type.includes('pdf')) return '#E53935';
    if (type.includes('doc') || type.includes('word')) return '#1565C0';
    if (type.includes('xls') || type.includes('excel') || type.includes('csv'))
      return '#2E7D32';
    if (type.includes('ppt')) return '#E65100';
    if (type.includes('zip') || type.includes('rar')) return '#6A1B9A';
    if (type.includes('txt')) return '#546E7A';
    if (type.includes('image') || type.includes('png') || type.includes('jpg'))
      return '#0288D1';
    return '#455A64'; // default fallback — yahi missing tha
  }
  getFileExt(m: any): string {
    const name = m.fileOriginalName || '';
    const ext = name.split('.').pop()?.toUpperCase() || '';
    return ext.length > 4 ? ext.substring(0, 4) : ext || 'FILE';
  }
  msgKey(m: ChatMessage): string {
    return String(m.id ?? m._id ?? m.timestamp);
  }
  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (this.showEmojiPicker) {
      const target = e.target as HTMLElement;
      if (
        !target.closest('.emoji-btn') &&
        !target.closest('.emoji-picker-container')
      ) {
        this.showEmojiPicker = false;
      }
    }
    if (this.showMembersPanel) {
      const t = e.target as HTMLElement;
      if (!t.closest('.members-popup') && !t.closest('.header-action-btn'))
        this.showMembersPanel = false;
      this.showInlineAddMember = false;
    }

    if (this.searchActive) {
      const s = e.target as HTMLElement;
      if (!s.closest('.msg-search-wrap') && !s.closest('.header-action-btn'))
        this.searchActive = false;
    }
    // ── Close group search dropdown on outside click ──────────────────
    if (this.showGroupSuggestions) {
      const g = e.target as HTMLElement;
      if (!g.closest('.group-search-container')) {
        this.showGroupSuggestions = false;
        this.activeSuggestionIndex = -1;
      }
    }
    // Close add-member suggestion dropdown on outside click
    if (this.addMemberSuggestions.length > 0) {
      const a = e.target as HTMLElement;
      if (!a.closest('.add-member-search-wrap')) this.addMemberSuggestions = [];
    }
  }

  // Member display helpers  ★ NEW ★
  // ─────────────────────────────────────────────────────────────────────────

  formatMemberList(room: ChatRoom): string {
    const details = room.memberDetails ?? [];
    if (details.length === 0) {
      // Fallback: show raw count from memberUserIds
      const cnt = room.memberUserIds?.length ?? 0;
      return cnt > 0 ? `${cnt} member${cnt !== 1 ? 's' : ''}` : 'No members';
    }

    const names = details.map((m) => m.displayName);
    if (names.length <= this.MEMBER_DISPLAY_LIMIT) return names.join(', ');
    return `${names.slice(0, this.MEMBER_DISPLAY_LIMIT).join(', ')} +${names.length - this.MEMBER_DISPLAY_LIMIT} more`;
  }

  fullMemberList(room: ChatRoom): string {
    return (room.memberDetails ?? []).map((m) => m.displayName).join(', ');
  }

  private applyMemberUpdate(
    roomId: string,
    memberDetails: MemberDetail[],
  ): void {
    const room = this.rooms.find((r) => r.roomId === roomId);
    if (room) {
      room.memberDetails = memberDetails;
      room.memberUserIds = memberDetails.map((m) => m.userId);
    }
    if (this.selectedRoom?.roomId === roomId) {
      this.selectedRoom.memberDetails = memberDetails;
      this.selectedRoom.memberUserIds = memberDetails.map((m) => m.userId);
    }
  }
  private handleRoomRemoved(roomId: string): void {
    // 1. Unsubscribe from the room topic so we stop receiving messages
    const sub = this.roomSubs.get(roomId);
    if (sub) {
      try {
        sub.unsubscribe();
      } catch {}
      this.roomSubs.delete(roomId);
    }

    // 2. Remove from room lists
    this.rooms = this.rooms.filter((r) => r.roomId !== roomId);
    this.filteredRooms = this.filteredRooms.filter((r) => r.roomId !== roomId);

    // 3. Clear view if this room was open
    if (this.selectedRoom?.roomId === roomId) {
      this.selectedRoom = undefined;
      this.messages = [];
      this.searchActive = false;
      this.showMembersPanel = false;
    }

    this.applyRoomSearch();
  }
  get filteredPanelMembers(): MemberDetail[] {
    const q = this.memberSearchQuery.trim().toLowerCase();
    const members = this.selectedRoom?.memberDetails ?? [];
    if (!q) return members;
    return members.filter(
      (m) =>
        m.displayName.toLowerCase().includes(q) ||
        m.userId.toLowerCase().includes(q),
    );
  }
  toggleMembersPanel(): void {
    this.showMembersPanel = !this.showMembersPanel;
    if (!this.showMembersPanel) {
      this.memberSearchQuery = '';
      this.showInlineAddMember = false;
      this.addMemberSearchQuery = '';
      this.selectedAddMemberUser = null;
      this.addMemberMessage = '';
    }
  }

  // ── Per-chat file state key ───────────────────────────────────────
  get chatKey(): string {
    if (this.selectedUser) return 'u:' + this.selectedUser.userId;
    if (this.selectedRoom) return 'r:' + this.selectedRoom.roomId;
    return '';
  }

  get pendingFile(): File | null {
    const k = this.chatKey;
    return k ? (this.pendingFiles.get(k) ?? null) : null;
  }
  set pendingFile(f: File | null) {
    const k = this.chatKey;
    if (!k) return;
    if (f) {
      this.pendingFiles.set(k, f);
      // ⭐ Persist to IndexedDB so file survives page refresh
      if (this.me) {
        this.fileDraftDb
          .save(`${this.me}:${k}`, f)
          .catch((e) => console.warn('Failed to persist file draft', e));
      }
    } else {
      this.pendingFiles.delete(k);
      // ⭐ Remove from IndexedDB when file is cleared or sent
      if (this.me) {
        this.fileDraftDb
          .delete(`${this.me}:${k}`)
          .catch((e) => console.warn('Failed to remove file draft', e));
      }
    }
  }

  get pendingFileError(): string | null {
    const k = this.chatKey;
    return k ? (this.pendingFileErrors.get(k) ?? null) : null;
  }
  set pendingFileError(e: string | null) {
    const k = this.chatKey;
    if (!k) return;
    if (e) this.pendingFileErrors.set(k, e);
    else this.pendingFileErrors.delete(k);
  }

  /** Object-URL for previewing image file drafts inline. */
  get pendingFilePreviewUrl(): string | null {
    const k = this.chatKey;
    return k ? (this.pendingFilePreviewUrls.get(k) ?? null) : null;
  }
  set pendingFilePreviewUrl(url: string | null) {
    const k = this.chatKey;
    if (!k) return;
    // Revoke any existing object URL to avoid memory leaks
    const existing = this.pendingFilePreviewUrls.get(k);
    if (existing) URL.revokeObjectURL(existing);
    if (url) this.pendingFilePreviewUrls.set(k, url);
    else this.pendingFilePreviewUrls.delete(k);
  }
  // ── Reply feature ─────────────────────────────────────────────────────

  startReply(msg: ChatMessage): void {
    this.replyingTo = msg;
    setTimeout(() => {
      const ta = document.querySelector('.msg-textarea') as HTMLTextAreaElement;
      ta?.focus();
    }, 60);
  }

  cancelReply(): void {
    this.replyingTo = null;
  }

  private buildReplyContext(msg: ChatMessage): ReplyContext {
    return {
      id: String(msg.id ?? msg._id ?? msg.timestamp),
      fromEmailId: msg.fromEmailId,
      fromName: msg.fromName ?? msg.fromEmailId.split('@')[0],
      content: (msg.content || '').slice(0, 120),
      fileOriginalName: msg.fileOriginalName,
      fileType: msg.fileType,
    };
  }

  scrollToOriginal(replyTo: ReplyContext): void {
    if (!this.msgBox) return;
    const wrappers: NodeListOf<HTMLElement> =
      this.msgBox.nativeElement.querySelectorAll('.msg-wrapper');
    let targetIndex = -1;
    wrappers.forEach((el, i) => {
      if (el.dataset['msgId'] === replyTo.id) targetIndex = i;
    });
    if (targetIndex === -1) {
      targetIndex = this.messages.findIndex(
        (m) =>
          m.fromEmailId === replyTo.fromEmailId &&
          (m.content || '').startsWith((replyTo.content || '').slice(0, 60)),
      );
    }
    if (targetIndex === -1 || !wrappers[targetIndex]) return;
    const el = wrappers[targetIndex];
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('reply-highlight');
    setTimeout(() => el.classList.remove('reply-highlight'), 1400);
  }

  replyAuthorLabel(replyTo: ReplyContext | ChatMessage | null): string {
    if (!replyTo) return '';
    if (replyTo.fromEmailId === this.me) return 'You';
    return replyTo.fromName ?? replyTo.fromEmailId.split('@')[0];
  }

  get replyingTo(): ChatMessage | null {
    const k = this.chatKey;
    return k ? (this.replyingToMap.get(k) ?? null) : null;
  }
  set replyingTo(msg: ChatMessage | null) {
    const k = this.chatKey;
    if (!k) return;
    if (msg) this.replyingToMap.set(k, msg);
    else this.replyingToMap.delete(k);
  }
  get newMessage(): string {
    return this.draftMap.get(this.chatKey) ?? '';
  }
  // set newMessage(val: string) {
  //   if (this.chatKey) this.draftMap.set(this.chatKey, val);
  // }
  set newMessage(val: string) {
    if (!this.chatKey) return;
    if (val && val.trim()) {
      this.draftMap.set(this.chatKey, val);
    } else {
      this.draftMap.delete(this.chatKey);
      this.draftSavedAt.delete(this.chatKey);
    }
    this.saveDraftsToStorage();
  }
  private _restoreDone = false;

  private saveActiveChat(tab: Tab, id: string): void {
    if (!this.me) return;
    try {
      localStorage.setItem(
        this.activeChatStorageKey,
        JSON.stringify({ tab, id }),
      );
    } catch {
      // localStorage unavailable in some sandboxed environments — silently skip
    }
  }
  private tryRestoreActiveChat(): void {
    // Only run once, and only when no chat is already open (e.g. user clicked
    // something themselves before the async loads returned).
    if (this._restoreDone || this.selectedUser || this.selectedRoom) return;

    let saved: { tab: Tab; id: string } | null = null;
    try {
      const raw = localStorage.getItem(this.activeChatStorageKey);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    if (!saved?.tab || !saved?.id) return;

    if (saved.tab === 'private') {
      // Need users list loaded — check it is available
      const user = this.users.find((u) => u.userId === saved!.id);
      if (!user) return; // loadUsers not done yet; will retry from loadUsers callback
      this._restoreDone = true;
      this.activeTab = 'private';
      this.selectUser(user);
    } else if (saved.tab === 'group') {
      // Need rooms list loaded — check it is available
      const room = this.rooms.find((r) => r.roomId === saved!.id);
      if (!room) return; // loadRooms not done yet; will retry from loadRooms callback
      this._restoreDone = true;
      this.activeTab = 'group';
      this.selectRoom(room);
    }
  }
  // ✨ Load drafts from localStorage after 'me' is known
  private loadDraftsFromStorage(): void {
    if (!this.me) return;
    const key = `chat_drafts_${this.me}`;
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const obj = JSON.parse(raw);
        const MAX_AGE = 24 * 60 * 60 * 1000; // 24 ghante
        const now = Date.now();
        let changed = false;
        for (const [chatId, val] of Object.entries(obj)) {
          // Support both old format (plain string) and new format ({ text, savedAt })
          if (typeof val === 'string') {
            // Old format — no timestamp, load once then it will get savedAt on next save
            this.draftMap.set(chatId, val);
          } else if (val && typeof (val as any).text === 'string') {
            const entry = val as { text: string; savedAt: number };
            if (now - entry.savedAt > MAX_AGE) {
              // Draft 7 din se purana — discard karo
              changed = true;
            } else {
              this.draftMap.set(chatId, entry.text);
              this.draftSavedAt.set(chatId, entry.savedAt);
            }
          }
        }
        // Agar koi stale draft remove hua toh localStorage update karo
        if (changed) this.saveDraftsToStorage();
      } catch (e) {
        console.warn('Failed to load drafts', e);
      }
    }
    // ⭐ Restore persisted file drafts from IndexedDB
    this.loadFileDraftsFromDb();
  }

  /** Restore pending file drafts from IndexedDB after login. */
  private loadFileDraftsFromDb(): void {
    if (!this.me) return;
    // ⭐ Clean up stale drafts (>24h) first to avoid storage bloat
    this.fileDraftDb.cleanupStaleDrafts().catch(() => {});
    this.fileDraftDb
      .loadAllForUser(this.me)
      .then((map) => {
        for (const [chatKey, file] of map.entries()) {
          // Restore in-memory file reference (does NOT re-trigger IndexedDB save)
          this.pendingFiles.set(chatKey, file);
          // Regenerate image preview URL for restored image drafts
          if (file.type.startsWith('image/')) {
            this.pendingFilePreviewUrls.set(chatKey, URL.createObjectURL(file));
          }
        }
      })
      .catch((e) => console.warn('Failed to restore file drafts', e));
  }

  // 💾 Save drafts to localStorage
  private saveDraftsToStorage(): void {
    if (!this.me) return;
    const key = `chat_drafts_${this.me}`;
    const obj: Record<string, { text: string; savedAt: number }> = {};
    for (const [k, v] of this.draftMap.entries()) {
      if (v && v.trim()) {
        const existing = this.draftSavedAt.get(k);
        const savedAt = existing ?? Date.now();
        if (!existing) this.draftSavedAt.set(k, savedAt);
        obj[k] = { text: v, savedAt };
      }
    }
    localStorage.setItem(key, JSON.stringify(obj));
  }
  // In ChatComponent class
  /** Opens the Bootstrap leave-group confirmation modal. */
  openLeaveGroupModal(): void {
    if (!this.selectedRoom) return;
    this.showLeaveGroupModal = true;
  }

  /** Dismisses the leave-group modal without action. */
  closeLeaveGroupModal(): void {
    this.showLeaveGroupModal = false;
  }

  /** Called when user clicks "Leave" inside the confirmation modal. */
  leaveGroup(): void {
    if (!this.selectedRoom || this.leavingGroup) return;

    this.leavingGroup = true;

    this.http
      .post<any>(this.url('/ctl/chat/room/leave'), {
        roomId: this.selectedRoom.roomId,
      })
      .subscribe({
        next: (res) => {
          this.leavingGroup = false;
          this.showLeaveGroupModal = false;

          if (res?.success) {
            if (this.selectedRoom) {
              this.handleRoomRemoved(this.selectedRoom.roomId);
            }
          } else {
            alert(res?.message || 'Could not leave group.');
          }
        },
        error: (err) => {
          this.leavingGroup = false;
          this.showLeaveGroupModal = false;
          console.error(err);
          alert(err?.error?.message || 'Server error. Please try again.');
        },
      });
  }
}
